import { useEffect, useRef } from 'react';
import { useTimeclockAdminStore } from '../store/useTimeclockAdminStore';
import { usePlanningStore } from '../store/usePlanningStore';
import { useAbsenceStore } from '../store/useAbsenceStore';
import { useEmployeeStore } from '../store/useEmployeeStore';
import { useDueTasksStore } from '../store/useDueTasksStore';
import { useOpenItemsStore } from '../store/useOpenItemsStore';
import { getMondayOfWeek } from '../utils/dashboardData';
import { isoDate } from '../utils/dateUtils';
import { getISOWeek, getISOWeekYear } from 'date-fns';

interface Options {
  pollMs?: number;
}

export function useDashboardOps({ pollMs = 60_000 }: Options = {}) {
  const fetchDailyStatus = useTimeclockAdminStore((s) => s.fetchDailyStatus);
  const fetchWeekEntries = usePlanningStore((s) => s.fetchWeekEntries);
  const fetchAbsences = useAbsenceStore((s) => s.fetchAbsences);
  const fetchEmployees = useEmployeeStore((s) => s.fetchEmployees);
  const fetchDueTasks = useDueTasksStore((s) => s.fetchDueTasks);
  const refreshMyOpenItems = useOpenItemsStore((s) => s.refreshMyCount);

  const didInit = useRef(false);

  useEffect(() => {
    const today = new Date();
    const todayIso = isoDate(today);
    const monday = getMondayOfWeek(today);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const mondayIso = isoDate(monday);
    const sundayIso = isoDate(sunday);

    const loadAll = async () => {
      const tasks: Promise<unknown>[] = [
        fetchDailyStatus(todayIso),
        fetchWeekEntries(mondayIso, sundayIso),
        fetchAbsences({ approvalStatus: 'Pending' }),
        refreshMyOpenItems(),
      ];
      if (!didInit.current) {
        tasks.push(
          fetchEmployees({ status: 'active', pageSize: 200 }),
          fetchDueTasks(getISOWeekYear(today), getISOWeek(today)),
        );
        didInit.current = true;
      }
      await Promise.allSettled(tasks);
    };

    loadAll();
    const id = setInterval(loadAll, pollMs);
    return () => clearInterval(id);
  }, [
    fetchDailyStatus,
    fetchWeekEntries,
    fetchAbsences,
    fetchEmployees,
    fetchDueTasks,
    refreshMyOpenItems,
    pollMs,
  ]);
}
